// Enhanced Vehicles V2 - Single Source of Truth Architecture Implementation
// Production-ready component that eliminates React error #185

import React, { useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Tabs, 
  Tab, 
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import { StateManagementBoundary } from '../components/error/StateManagementBoundary';
import { TabsErrorBoundary } from '../components/error/TabsErrorBoundary';
import { useVehicles } from '../hooks/useVehicles';
import { 
  useVehicleEnhancedStore,
  useVehicleSelection,
  useVehicleFilters,
  useVehicleView
} from '../store/vehicleEnhancedStore';

/**
 * EnhancedVehiclesV2 - Unified VRP Architecture Implementation
 * Migrated from legacy StableProject system to unified VRP Navigation Context
 * Maintains existing functionality while eliminating dual context conflicts
 */
export const EnhancedVehiclesV2: React.FC = () => {
  // Unified VRP Navigation Context - single source of truth
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  // Vehicle data from unified hooks
  const { 
    data: vehicles = [], 
    isLoading: vehiclesLoading, 
    error: vehiclesError,
    refetch: refetchVehicles 
  } = useVehicles();
  
  // Map legacy loading states for compatibility
  const projectLoading = isLoading.navigation;
  const isInitialLoading = vehiclesLoading;
  const isRefreshing = false; // TODO: Implement refresh indicator if needed
  const hasData = vehicles.length > 0;
  const isEmpty = !vehiclesLoading && vehicles.length === 0;
  const hasError = !!vehiclesError;
  const errorMessage = vehiclesError?.message || 'Failed to load vehicles';
  const vehicleCount = vehicles.length;
  
  // Layer 4: Enhanced State (UI Features) - Optimized selectors
  const { 
    selectedVehicles, 
    toggleVehicleSelection, 
    selectAll, 
    clearSelection 
  } = useVehicleSelection();
  
  const { 
    enhancedFilters, 
    filterVehicles 
  } = useVehicleFilters();
  
  const { 
    viewMode, 
    sortVehicles 
  } = useVehicleView();
  
  // Enhanced vehicle processing (unified architecture)
  const displayVehicles = useMemo(() => {
    if (!vehicles) return [];
    
    const filtered = filterVehicles(vehicles);
    const sorted = sortVehicles(filtered);
    
    return sorted;
  }, [vehicles, filterVehicles, sortVehicles]);
  
  const selectionStats = useMemo(() => {
    if (!displayVehicles.length) return null;
    
    const selectedData = displayVehicles.filter(v => selectedVehicles.has(v.id));
    return {
      count: selectedData.length,
      totalCapacity: selectedData.reduce((sum, v) => sum + (v.capacity_array?.[0] || 0), 0)
    };
  }, [displayVehicles, selectedVehicles]);
  
  // Tab labels with counts
  const tabLabels = useMemo(() => {
    const all = displayVehicles.length;
    const multiCapacity = displayVehicles.filter(v => 
      (v.capacity_array?.length || 0) > 1
    ).length;
    const withSkills = displayVehicles.filter(v => 
      (v.skills_detailed?.length || 0) > 0
    ).length;
    const vroomCompatible = displayVehicles.filter(v => 
      v.vroom_compatible?.overall_compatible
    ).length;
    
    return [
      `All Vehicles (${all})`,
      `Multi-Capacity (${multiCapacity})`,
      `Skills & Certifications (${withSkills})`,
      `VROOM Compatible (${vroomCompatible})`
    ];
  }, [displayVehicles]);
  
  // ENHANCED TAB NAVIGATION with React Error #185 Prevention
  const [activeTab, setActiveTab] = React.useState(0);
  
  // DEFENSIVE TAB VALIDATION to prevent React Error #185
  const validateTabValue = (value: any): number => {
    // Comprehensive validation to prevent invalid tab values
    if (typeof value !== 'number') {
      console.warn('[EnhancedVehiclesV2] Invalid tab value type:', typeof value, value);
      return 0;
    }
    
    if (isNaN(value) || !isFinite(value)) {
      console.warn('[EnhancedVehiclesV2] Invalid tab value (NaN/Infinite):', value);
      return 0;
    }
    
    if (value < 0) {
      console.warn('[EnhancedVehiclesV2] Negative tab value:', value);
      return 0;
    }
    
    if (value >= tabLabels.length) {
      console.warn('[EnhancedVehiclesV2] Tab value exceeds available tabs:', value, 'max:', tabLabels.length - 1);
      return Math.max(0, tabLabels.length - 1);
    }
    
    return Math.floor(value); // Ensure integer
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    try {
      const validatedValue = validateTabValue(newValue);
      console.log('[EnhancedVehiclesV2] Tab change:', { requested: newValue, validated: validatedValue });
      setActiveTab(validatedValue);
    } catch (error) {
      console.error('[EnhancedVehiclesV2] ERROR in tab change handler:', error);
      // Emergency fallback
      setActiveTab(0);
    }
  };
  
  // Early returns for error states
  if (!currentProject) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        <Typography>No project selected. Please select a project to view enhanced vehicles.</Typography>
      </Alert>
    );
  }
  
  if (hasError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="h6" gutterBottom>
          Failed to Load Vehicles
        </Typography>
        <Typography>{errorMessage}</Typography>
        <Button 
          onClick={() => refetchVehicles()} 
          variant="contained" 
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Alert>
    );
  }
  
  // Loading state
  if (isInitialLoading || projectLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading enhanced vehicles...</Typography>
      </Box>
    );
  }
  
  // Render vehicle card
  const renderVehicleCard = (vehicle: any) => {
    const isSelected = selectedVehicles.has(vehicle.id);
    const vroomStatus = vehicle.vroom_compatible?.overall_compatible;
    const capacity = vehicle.capacity_array?.[0] || 0;
    const skillsCount = vehicle.skills_detailed?.length || 0;
    
    return (
      <Card 
        key={vehicle.id}
        sx={{ 
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)'
          }
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h3" noWrap sx={{ flex: 1, mr: 1 }}>
              {vehicle.name || `Vehicle ${vehicle.id}`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {vroomStatus ? (
                <Tooltip title="VROOM Compatible">
                  <CheckCircleIcon color="success" fontSize="small" />
                </Tooltip>
              ) : (
                <Tooltip title="VROOM Issues">
                  <WarningIcon color="warning" fontSize="small" />
                </Tooltip>
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {vehicle.vehicle_type && (
              <Chip 
                label={vehicle.vehicle_type} 
                size="small" 
                variant="outlined" 
              />
            )}
            {capacity > 0 && (
              <Chip 
                label={`Capacity: ${capacity}`} 
                size="small" 
                color="primary" 
              />
            )}
            {skillsCount > 0 && (
              <Chip 
                label={`${skillsCount} Skills`} 
                size="small" 
                color="secondary" 
              />
            )}
          </Box>
          
          {vehicle.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {vehicle.description.length > 100 
                ? `${vehicle.description.substring(0, 100)}...`
                : vehicle.description
              }
            </Typography>
          )}
          
          {vehicle.vroom_compatible && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" gutterBottom>
                VROOM Compatibility: {Math.round(vehicle.vroom_compatible.compatibility_score || 0)}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={vehicle.vroom_compatible.compatibility_score || 0}
                color={vroomStatus ? 'success' : 'warning'}
              />
            </Box>
          )}
        </CardContent>
        
        <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
          <Button
            size="small"
            startIcon={isSelected ? <CheckCircleIcon /> : null}
            onClick={() => toggleVehicleSelection(vehicle.id)}
            variant={isSelected ? 'contained' : 'outlined'}
          >
            {isSelected ? 'Selected' : 'Select'}
          </Button>
          
          <Box>
            <Tooltip title="View Details">
              <IconButton size="small">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Vehicle">
              <IconButton size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardActions>
      </Card>
    );
  };
  
  return (
    <StateManagementBoundary layer="Enhanced Vehicles V2 Component">
      <ProfessionalLayout
        pageTitle="Enhanced Vehicles V2"
        pageSubtitle={`Project: ${currentProject?.name} • ${vehicleCount} vehicles`}
        activeTab="Enhanced Vehicles"
      >
        {/* Tab Navigation with Error Protection */}
        <TabsErrorBoundary 
          fallbackTab={0}
          onTabError={(error) => console.error('🚨 Tab error caught:', error)}
        >
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={validateTabValue(activeTab)} // DEFENSIVE: Validate before passing to Tabs
              onChange={handleTabChange}
              variant="standard"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              // DEFENSIVE PROPS to prevent React Error #185
              indicatorColor="primary"
              textColor="primary"
              aria-label="Enhanced Vehicles Tabs"
            >
              {tabLabels.length > 0 ? tabLabels.map((label, index) => (
                <Tab 
                  key={`tab-${index}-${label.substring(0, 10)}`} // More stable key
                  label={label || `Tab ${index + 1}`} // Fallback label
                  id={`enhanced-vehicles-tab-${index}`}
                  aria-controls={`enhanced-vehicles-tabpanel-${index}`}
                  // DEFENSIVE: Prevent invalid label props
                  {...(typeof label === 'string' && label.trim() ? {} : { disabled: true })}
                />
              )) : (
                // Fallback tab if no labels
                <Tab key="fallback-tab" label="All Vehicles" />
              )}
            </Tabs>
          </Paper>
        </TabsErrorBoundary>
        
        {/* Status Bar */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {displayVehicles.length} Vehicles
              {isRefreshing && (
                <Chip 
                  label="Refreshing..." 
                  size="small" 
                  color="info" 
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            {selectionStats && selectionStats.count > 0 && (
              <Typography variant="body2" color="text.secondary">
                {selectionStats.count} selected • Total capacity: {selectionStats.totalCapacity}
              </Typography>
            )}
          </Box>
          
          {selectedVehicles.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                onClick={() => selectAll(displayVehicles.map(v => v.id))}
                variant="outlined"
                size="small"
              >
                Select All ({displayVehicles.length})
              </Button>
              <Button 
                onClick={clearSelection}
                variant="outlined"
                size="small"
              >
                Clear Selection
              </Button>
            </Box>
          )}
        </Box>
        
        {/* Empty State */}
        {isEmpty && (
          <Alert severity="info" sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              No Vehicles Found
            </Typography>
            <Typography>
              No vehicles match the current filters for this project.
            </Typography>
          </Alert>
        )}
        
        {/* Vehicles Grid */}
        {hasData && (
          <Grid container spacing={3}>
            {displayVehicles.map(vehicle => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={vehicle.id}>
                {renderVehicleCard(vehicle)}
              </Grid>
            ))}
          </Grid>
        )}
      </ProfessionalLayout>
    </StateManagementBoundary>
  );
};

export default EnhancedVehiclesV2;